import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Crown, UserPlus, Award, CheckCircle2, MapPin, Briefcase,
  GraduationCap, Heart, Send, Loader2, ArrowLeft, Shield,
  Scale, Calculator, Building2, Home, Target, FileText, Bot
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AREA_ICONS = {
  legal_inmobiliario: Scale,
  fiscal_inmobiliario: Calculator,
  financiera_inmobiliaria: Target,
  comercial_industrial: Building2,
  desarrollo_inmobiliario: Home,
  valuacion: Calculator,
  hipotecario: Home,
  notarial: FileText,
  tecnologia_ia: Bot
};

const ESTADOS_MEXICO = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
  'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

const CARGOS_CONSEJO = [
  { value: 'presidente_regional', label: 'Presidente Regional' },
  { value: 'director_eventos', label: 'Director de Eventos' },
  { value: 'director_vinculacion', label: 'Director de Vinculación' },
  { value: 'director_relaciones_publicas', label: 'Director de Relaciones Públicas' },
  { value: 'tesorero', label: 'Tesorero' },
  { value: 'administrador_regional', label: 'Administrador Regional' }
];

const PostulacionLiderazgoPage = () => {
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [areas, setAreas] = useState([]);
  const [misPostulaciones, setMisPostulaciones] = useState([]);
  const [step, setStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    tipo_posicion: '', // director_area o consejo_regional
    area_id: '',
    estado: '',
    cargo_solicitado: '',
    trayectoria_profesional: '',
    anos_experiencia: '',
    especializacion: '',
    motivacion: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para postularte como líder UPIM",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('upim_token');
      const [areasRes, postulacionesRes] = await Promise.all([
        axios.get(`${API_URL}/api/institutional/areas`),
        axios.get(`${API_URL}/api/institutional/postulacion/mis-postulaciones`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAreas(areasRes.data.areas || []);
      setMisPostulaciones(postulacionesRes.data.postulaciones || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tipo_posicion || !formData.trayectoria_profesional || !formData.motivacion) {
      toast({
        title: "Campos incompletos",
        description: "Por favor completa todos los campos requeridos",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('upim_token');
      await axios.post(
        `${API_URL}/api/institutional/postulacion`,
        {
          ...formData,
          anos_experiencia: parseInt(formData.anos_experiencia) || 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Postulación enviada",
        description: "Tu postulación ha sido recibida. El equipo de UPIM la revisará y te contactará pronto."
      });

      // Reset form and reload
      setFormData({
        tipo_posicion: '',
        area_id: '',
        estado: '',
        cargo_solicitado: '',
        trayectoria_profesional: '',
        anos_experiencia: '',
        especializacion: '',
        motivacion: ''
      });
      setStep(1);
      loadData();
    } catch (error) {
      console.error('Error submitting:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo enviar tu postulación",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const hasPendingPostulacion = misPostulaciones.some(p => p.status === 'pendiente');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50" data-testid="postulacion-liderazgo-page">
        {/* Hero */}
        <div className="bg-gradient-to-br from-amber-600 via-amber-500 to-yellow-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <Button 
              variant="ghost" 
              className="text-white/80 hover:text-white mb-6"
              onClick={() => navigate('/directorio')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Directorio
            </Button>
            
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-white/20 text-white mb-4">
                <Crown className="w-4 h-4 mr-1" />
                Convocatoria Abierta
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                Postúlate como Líder UPIM
              </h1>
              <p className="text-lg text-amber-100">
                Buscamos profesionales con trayectoria, ética y prestigio para representar a UPIM 
                como Directores de Área o miembros del Consejo Regional.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Beneficios */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <Card className="text-center p-6">
              <Award className="w-10 h-10 mx-auto text-amber-500 mb-3" />
              <h3 className="font-semibold text-gray-900">Distintivo Oficial</h3>
              <p className="text-sm text-gray-600">Badge de Director o Consejo UPIM</p>
            </Card>
            <Card className="text-center p-6">
              <Shield className="w-10 h-10 mx-auto text-indigo-500 mb-3" />
              <h3 className="font-semibold text-gray-900">50% Descuento</h3>
              <p className="text-sm text-gray-600">En membresía y servicios UPIM</p>
            </Card>
            <Card className="text-center p-6">
              <Crown className="w-10 h-10 mx-auto text-purple-500 mb-3" />
              <h3 className="font-semibold text-gray-900">Visibilidad Nacional</h3>
              <p className="text-sm text-gray-600">Aparece en el directorio público</p>
            </Card>
          </div>

          {/* Postulaciones existentes */}
          {misPostulaciones.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-lg">Mis Postulaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {misPostulaciones.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">
                          {p.tipo === 'director_area' ? 'Director de Área' : 'Consejo Regional'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {p.estado || 'Nacional'} - {new Date(p.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                      <Badge className={
                        p.status === 'aprobada' ? 'bg-green-100 text-green-700' :
                        p.status === 'rechazada' ? 'bg-red-100 text-red-700' :
                        p.status === 'revision' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {p.status === 'aprobada' ? 'Aprobada' :
                         p.status === 'rechazada' ? 'Rechazada' :
                         p.status === 'revision' ? 'En revisión' : 'Pendiente'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulario */}
          {hasPendingPostulacion ? (
            <Card className="text-center p-8">
              <div className="w-16 h-16 mx-auto bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Ya tienes una postulación pendiente</h3>
              <p className="text-gray-600">
                Tu postulación está siendo revisada por el equipo de UPIM. Te contactaremos pronto.
              </p>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Formulario de Postulación</CardTitle>
                <CardDescription>Completa la información para postularte como líder UPIM</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Paso 1: Tipo de posición */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700">
                        ¿Qué tipo de posición te interesa?
                      </label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, tipo_posicion: 'director_area' });
                            setStep(2);
                          }}
                          className={`p-6 rounded-xl border-2 text-left transition-all ${
                            formData.tipo_posicion === 'director_area'
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Crown className="w-8 h-8 text-amber-500 mb-3" />
                          <h4 className="font-semibold text-gray-900">Director de Área</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Líder especializado en un área (Legal, Fiscal, Comercial, etc.)
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, tipo_posicion: 'consejo_regional' });
                            setStep(2);
                          }}
                          className={`p-6 rounded-xl border-2 text-left transition-all ${
                            formData.tipo_posicion === 'consejo_regional'
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Shield className="w-8 h-8 text-indigo-500 mb-3" />
                          <h4 className="font-semibold text-gray-900">Consejo Regional</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Parte del gobierno regional (Presidente, Tesorero, etc.)
                          </p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Paso 2: Detalles de posición */}
                  {step === 2 && (
                    <div className="space-y-4">
                      <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Cambiar tipo de posición
                      </Button>

                      {formData.tipo_posicion === 'director_area' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Área de especialización *
                            </label>
                            <select
                              value={formData.area_id}
                              onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              required
                            >
                              <option value="">Selecciona un área</option>
                              {areas.filter(a => !a.has_director_nacional).map(area => (
                                <option key={area.id} value={area.id}>{area.nombre}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Estado (para Director Regional, dejar vacío para Nacional)
                            </label>
                            <select
                              value={formData.estado}
                              onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="">Nacional (sin estado específico)</option>
                              {ESTADOS_MEXICO.map(estado => (
                                <option key={estado} value={estado}>{estado}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}

                      {formData.tipo_posicion === 'consejo_regional' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Estado *
                            </label>
                            <select
                              value={formData.estado}
                              onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              required
                            >
                              <option value="">Selecciona tu estado</option>
                              {ESTADOS_MEXICO.map(estado => (
                                <option key={estado} value={estado}>{estado}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Cargo solicitado *
                            </label>
                            <select
                              value={formData.cargo_solicitado}
                              onChange={(e) => setFormData({ ...formData, cargo_solicitado: e.target.value })}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              required
                            >
                              <option value="">Selecciona un cargo</option>
                              {CARGOS_CONSEJO.map(cargo => (
                                <option key={cargo.value} value={cargo.value}>{cargo.label}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}

                      <Button type="button" onClick={() => setStep(3)} className="w-full">
                        Continuar
                      </Button>
                    </div>
                  )}

                  {/* Paso 3: Información profesional */}
                  {step === 3 && (
                    <div className="space-y-4">
                      <Button type="button" variant="ghost" onClick={() => setStep(2)}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Volver
                      </Button>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Briefcase className="w-4 h-4 inline mr-1" />
                          Trayectoria profesional *
                        </label>
                        <Textarea
                          value={formData.trayectoria_profesional}
                          onChange={(e) => setFormData({ ...formData, trayectoria_profesional: e.target.value })}
                          placeholder="Describe tu experiencia profesional en el sector inmobiliario..."
                          className="min-h-[120px]"
                          required
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Años de experiencia
                          </label>
                          <Input
                            type="number"
                            value={formData.anos_experiencia}
                            onChange={(e) => setFormData({ ...formData, anos_experiencia: e.target.value })}
                            placeholder="Ej: 10"
                            min="0"
                            max="50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <GraduationCap className="w-4 h-4 inline mr-1" />
                            Especialización
                          </label>
                          <Input
                            value={formData.especializacion}
                            onChange={(e) => setFormData({ ...formData, especializacion: e.target.value })}
                            placeholder="Ej: Derecho inmobiliario, valuación..."
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Heart className="w-4 h-4 inline mr-1" />
                          Motivación para ser líder UPIM *
                        </label>
                        <Textarea
                          value={formData.motivacion}
                          onChange={(e) => setFormData({ ...formData, motivacion: e.target.value })}
                          placeholder="¿Por qué quieres representar a UPIM? ¿Qué aportarías a la comunidad?"
                          className="min-h-[120px]"
                          required
                        />
                      </div>

                      <Button type="submit" className="w-full" disabled={submitting}>
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Enviar Postulación
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PostulacionLiderazgoPage;
